import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [impersonationData, setImpersonationData] = useState(null);

  // Initialize auth state from localStorage
  useEffect(() => {
    const storedToken = localStorage.getItem('s3lion_token');
    const storedUser = localStorage.getItem('s3lion_user');
    const storedImpersonation = localStorage.getItem('s3lion_impersonation');
    
    if (storedToken && storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setToken(storedToken);
        setUser(parsedUser);
        
        // Check for impersonation data
        if (storedImpersonation) {
          const parsedImpersonation = JSON.parse(storedImpersonation);
          setImpersonationData(parsedImpersonation);
        }
        
        // Set default authorization header
        axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
      } catch (error) {
        console.error('Error parsing stored user data:', error);
        localStorage.removeItem('s3lion_token');
        localStorage.removeItem('s3lion_user');
        localStorage.removeItem('s3lion_impersonation');
      }
    }
    
    setLoading(false);
  }, []);

  const login = async (credentials) => {
    try {
      setError(null);
      setLoading(true);
      
      const response = await axios.post('/auth/login', credentials);
      
      if (response.status === 200) {
        const { access_token, user: userData } = response.data;
        
        setToken(access_token);
        setUser(userData);
        
        // Store in localStorage
        localStorage.setItem('s3lion_token', access_token);
        localStorage.setItem('s3lion_user', JSON.stringify(userData));
        
        // Set default authorization header
        axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
        
        return { success: true };
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Login failed';
      setError(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  };

  const register = async (userData) => {
    try {
      setError(null);
      setLoading(true);
      
      const response = await axios.post('/auth/register', userData);
      
      if (response.status === 200) {
        // Registration successful, but user needs to login
        return { success: true, user: response.data };
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Registration failed';
      setError(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  };

  const loginWithMicrosoft = async () => {
    try {
      setError(null);
      setLoading(true);
      
      // Redirect to Microsoft OAuth endpoint
      window.location.href = '/auth/azure/login';
    } catch (error) {
      const message = 'Microsoft login failed';
      setError(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    setError(null);
    setImpersonationData(null);
    
    // Clear localStorage
    localStorage.removeItem('s3lion_token');
    localStorage.removeItem('s3lion_user');
    localStorage.removeItem('s3lion_impersonation');
    
    // Remove default authorization header
    delete axios.defaults.headers.common['Authorization'];
  };

  const startImpersonation = async (userId) => {
    try {
      setError(null);
      
      const response = await axios.post(`/api/admin/impersonate/${userId}`);
      
      if (response.status === 200) {
        const { impersonation_token, admin_user, target_user } = response.data;
        
        // Store the impersonation data
        const impersonationInfo = {
          adminUser: admin_user,
          targetUser: target_user,
          isImpersonating: true
        };
        
        setImpersonationData(impersonationInfo);
        setToken(impersonation_token);
        setUser(target_user);
        
        // Store in localStorage
        localStorage.setItem('s3lion_token', impersonation_token);
        localStorage.setItem('s3lion_user', JSON.stringify(target_user));
        localStorage.setItem('s3lion_impersonation', JSON.stringify(impersonationInfo));
        
        // Set default authorization header
        axios.defaults.headers.common['Authorization'] = `Bearer ${impersonation_token}`;
        
        return { success: true };
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Impersonation failed';
      setError(message);
      return { success: false, error: message };
    }
  };

  const stopImpersonation = async () => {
    try {
      setError(null);
      
      const response = await axios.post('/api/admin/stop-impersonation');
      
      if (response.status === 200) {
        const { access_token, user: adminUser } = response.data;
        
        // Clear impersonation data
        setImpersonationData(null);
        setToken(access_token);
        setUser(adminUser);
        
        // Update localStorage
        localStorage.setItem('s3lion_token', access_token);
        localStorage.setItem('s3lion_user', JSON.stringify(adminUser));
        localStorage.removeItem('s3lion_impersonation');
        
        // Set default authorization header
        axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
        
        return { success: true };
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Stop impersonation failed';
      setError(message);
      return { success: false, error: message };
    }
  };

  const getCurrentUser = async () => {
    try {
      if (!token) return null;
      
      const response = await axios.get('/auth/me');
      if (response.status === 200) {
        setUser(response.data);
        localStorage.setItem('s3lion_user', JSON.stringify(response.data));
        return response.data;
      }
    } catch (error) {
      console.error('Error fetching current user:', error);
      // If token is invalid, logout
      if (error.response?.status === 401) {
        logout();
      }
      return null;
    }
  };

  const isAuthenticated = () => {
    return !!(user && token);
  };

  const hasRole = (requiredRole) => {
    if (!user) return false;
    
    const roleHierarchy = {
      'admin': ['admin', 'reviewer', 'user'],
      'reviewer': ['reviewer', 'user'],
      'user': ['user']
    };
    
    return roleHierarchy[user.role]?.includes(requiredRole) || false;
  };

  const isAdmin = () => hasRole('admin');
  const isReviewer = () => hasRole('reviewer');

  // Helper function to get auth headers for API requests
  const getAuthHeaders = () => {
    // Priority: user JWT token > environment variable > default fallback
    const authToken = token || process.env.REACT_APP_API_AUTH_TOKEN || '12345';
    return {
      'Authorization': `Bearer ${authToken}`
    };
  };

  const value = {
    user,
    token,
    loading,
    error,
    impersonationData,
    login,
    register,
    loginWithMicrosoft,
    logout,
    startImpersonation,
    stopImpersonation,
    getCurrentUser,
    isAuthenticated,
    hasRole,
    isAdmin,
    isReviewer,
    getAuthHeaders,
    setError
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};